<script lang="ts">
	// The admin's shell (UC-20): a rail at the inline start with the mark, the
	// sections, who is signed in, the theme and Sign out; the page beside it.
	// Below 768 px the rail becomes a bar across the top, and the sections fold
	// behind the button that names the one on screen, as on the dashboard.
	//
	// Its sections, with their use cases: Overview and Accounts (UC-21), one
	// account (UC-23), Subscriptions (UC-24) and Support contact (UC-22). Each has a path, so a
	// reload stays where it was and Back works.

	import BrandMark from '../components/BrandMark.svelte';
	import Button from '../components/Button.svelte';
	import NavItem from '../components/NavItem.svelte';
	import Notice from '../components/Notice.svelte';
	import type { IconName } from '../components/Icon.svelte';
	import { setThemeChoice, themeChoice, type ThemeChoice } from '../display.svelte';
	import { currentPath, navigate } from '../router.svelte';
	import Account from './Account.svelte';
	import Accounts from './Accounts.svelte';
	import { copy } from './copy';
	import { clearFlash, currentFlash } from './flash.svelte';
	import { accountIdOf, accountOrigin } from './nav.svelte';
	import Overview from './Overview.svelte';
	import { signOut } from './session.svelte';
	import Subscriptions from './Subscriptions.svelte';
	import SupportContact from './SupportContact.svelte';

	interface Props {
		email: string;
	}

	let { email }: Props = $props();

	let menuOpen = $state(false);
	let leaving = $state(false);

	interface Section {
		path: string;
		label: string;
		icon: IconName;
		testId: string;
	}

	const overview: Section = {
		path: '/',
		label: copy.overview,
		icon: 'chart',
		testId: 'admin-nav-overview',
	};
	const sections: Section[] = [
		overview,
		{ path: '/accounts', label: copy.accounts, icon: 'store', testId: 'admin-nav-accounts' },
		{
			path: '/subscriptions',
			label: copy.subscriptions,
			icon: 'clock',
			testId: 'admin-nav-subscriptions',
		},
		{
			path: '/support',
			label: copy.supportContact,
			icon: 'phone',
			testId: 'admin-nav-support',
		},
	];

	// One account's page (A4) sits under the section it was opened from.
	const accountId = $derived(accountIdOf(currentPath()));
	const sectionPath = $derived(accountId ? accountOrigin() : currentPath());

	// An unknown path shows Overview: Pages serves this page for any path.
	const active = $derived(sections.find((s) => s.path === sectionPath) ?? overview);

	function go(path: string) {
		menuOpen = false;
		navigate(path);
	}

	// Light, then Dark, then Follow system, as the design's button cycles.
	const next: Record<ThemeChoice, ThemeChoice> = { light: 'dark', dark: 'system', system: 'light' };

	function nextTheme() {
		setThemeChoice(next[themeChoice()]);
	}

	async function leave() {
		leaving = true;
		await signOut();
	}
</script>

<div class="admin" class:open={menuOpen} data-testid="admin-shell">
	<aside>
		<div class="brand">
			<BrandMark size={26} />
			<span class="name">{copy.title}</span>
			<span class="toggle">
				<Button
					testId="admin-menu"
					label={active.label}
					icon="expand"
					tone="secondary"
					size="sm"
					onPress={() => (menuOpen = !menuOpen)}
				/>
			</span>
		</div>
		<nav data-testid="admin-nav">
			{#each sections as section (section.path)}
				<NavItem
					testId={section.testId}
					label={section.label}
					icon={section.icon}
					active={section.path === active.path}
					onPress={() => go(section.path)}
				/>
			{/each}
		</nav>
		<div class="foot">
			<span class="label">{copy.signedInAs}</span>
			<span class="email" data-testid="admin-email">{email}</span>
			<Button
				testId="admin-theme"
				label={copy.theme(themeChoice())}
				tone="text-quiet"
				size="sm"
				onPress={nextTheme}
			/>
			<Button
				testId="admin-sign-out"
				label={copy.signOut}
				tone="text-quiet"
				size="sm"
				state={leaving ? 'pending' : 'idle'}
				pendingLabel={copy.signOut}
				onPress={() => void leave()}
			/>
		</div>
	</aside>

	<main data-testid="admin-page">
		<div class="page">
			{#if accountId}
				<Account {accountId} />
			{:else}
				<h1>{active.label}</h1>
			{/if}
			<!-- Kept mounted under an account opened from it, so Back finds
			     the same search and the same place. -->
			{#if active.path === '/accounts'}
				<div class="keep" hidden={!!accountId}>
					<Accounts />
				</div>
			{:else if active.path === '/subscriptions'}
				<div class="keep" hidden={!!accountId}>
					<Subscriptions />
				</div>
			{:else if accountId}
				<!-- Opened from a section that has no list to keep. -->
			{:else if active.path === '/support'}
				<SupportContact />
			{:else}
				<Overview />
			{/if}
		</div>
	</main>

	{#if currentFlash()}
		{#key currentFlash()?.n}
			<Notice life="flash" tone="ok" onDismiss={clearFlash} testId="admin-flash">
				{currentFlash()?.text}
			</Notice>
		{/key}
	{/if}
</div>

<style>
	/* The rail's width is where a floating Notice starts, as the design's
	   shell sets it, so a flash centres over the page and not the rail. */
	.admin {
		--notice-inset-start: var(--sidebar-width);
		min-block-size: 100dvh;
		display: flex;
		flex-wrap: wrap;
		align-items: stretch;
		background: var(--surface-page);
		color: var(--text-strong);
	}

	aside {
		flex: none;
		inline-size: var(--sidebar-width);
		display: flex;
		flex-direction: column;
		gap: var(--space-9);
		padding: var(--space-9) 0;
		background: var(--surface-page);
		border-inline-end: var(--border-width) solid var(--border-default);
	}

	.brand {
		display: flex;
		align-items: center;
		gap: var(--space-4);
		padding: 0 var(--space-5);
	}

	.name {
		flex: 1;
		min-inline-size: 0;
		font-size: var(--text-md);
		font-weight: 700;
	}

	.toggle {
		display: none;
	}

	nav {
		display: flex;
		flex-direction: column;
		gap: var(--space-1);
		padding: 0 var(--space-3);
	}

	.foot {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: var(--space-2);
		padding: var(--space-5) var(--space-5) 0;
		margin-block-start: auto;
	}

	.label {
		font-size: var(--text-2xs);
		color: var(--text-muted);
	}

	.email {
		max-inline-size: 100%;
		font-size: var(--text-sm);
		font-weight: 600;
		overflow-wrap: anywhere;
	}

	main {
		flex: 999 1 520px;
		min-inline-size: 0;
		padding: var(--page-pad) clamp(16px, 3vw, 26px);
	}

	.page {
		max-inline-size: 1080px;
		inline-size: 100%;
		display: flex;
		flex-direction: column;
		gap: var(--space-9);
	}

	.keep {
		display: contents;
	}

	.keep[hidden] {
		display: none;
	}

	h1 {
		margin: 0;
		font-size: var(--text-h3);
		font-weight: 700;
		letter-spacing: var(--tracking-tight);
		line-height: var(--leading-headline);
	}

	/* A narrow screen: the rail is a bar across the top, and its contents
	   fold behind the button that names the section on screen. */
	@media (max-width: 767px) {
		/* The bar and the page wrap into two rows. Packed at the top, or the
		   rows share the screen's spare height and a short page leaves the
		   bar half the screen tall. */
		.admin {
			--notice-inset-start: 0px;
			align-content: flex-start;
		}

		aside {
			flex: 1 1 100%;
			inline-size: auto;
			gap: var(--space-5);
			padding: var(--space-5) var(--space-3);
			border-inline-end: none;
			border-block-end: var(--border-width) solid var(--border-default);
		}

		.toggle {
			display: block;
		}

		nav,
		.foot {
			display: none;
		}

		.foot {
			margin-block-start: 0;
		}

		.open nav,
		.open .foot {
			display: flex;
		}
	}
</style>
